import * as React from 'react'

import { cn } from '@/lib/utils'
import OptionsPopoverShell from '~/client/src/shared/components/OptionsList/shells/OptionsPopoverShell'
import { SHEET_INSET_CLASS } from '~/client/src/shared/components/OptionsList/surfaceInset'
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from '~/client/src/shared/components/shadcn-ui/drawer'

// A definite, positioned box for children that size themselves with height:100%
// (emoji-picker-react writes it inline).
const FILL_BOX_CLASS =
  'tw:relative tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden tw:rounded-[inherit]'
const SHEET_FILL_CLASS = 'tw:h-[60vh]'
// max-h-none: the popover shell's min(640px,--available-height) cap is a
// different tailwind-merge group from h-*, so without it a fill surface would
// silently clip on a short viewport.
const POPOVER_FILL_CLASS = 'tw:h-[420px] tw:max-h-none tw:p-0'
const POPOVER_WIDE_CLASS =
  'tw:w-[min(700px,60vw)] tw:max-h-[min(700px,60vh)] tw:p-0'
const POPOVER_PICKER_CLASS = 'tw:w-[400px]'
// A picker's box grows with what it shows, up to the shell's cap: a short list stays short and a
// long one gets the full height, rather than both sharing one fixed size.
const POPOVER_PICKER_FILL_CLASS = 'tw:p-0'

export interface IResponsiveSurfaceProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Accessible name: sr-only DrawerTitle on mobile, aria-label on desktop.
  title: string
  children: React.ReactNode

  isMobileMode?: boolean
  // Pass trigger OR anchor, never both.
  trigger?: React.ReactNode
  anchor?: HTMLElement
  // 'fill' hands children a definite box; 'content' sizes to content under the
  // surface's own cap.
  sizing?: 'content' | 'fill'
  /** `wide` for list surfaces needing more than the default 320px; `picker` is 400px. */
  width?: 'default' | 'wide' | 'picker'
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
}

const ResponsiveSurface: React.FC<IResponsiveSurfaceProps> = ({
  open,
  onOpenChange,
  title,
  children,
  isMobileMode,
  trigger,
  anchor,
  sizing = 'content',
  width = 'default',
  side = 'bottom',
  align = 'start',
  sideOffset = 8,
}) => {
  const isFill = sizing === 'fill'
  const body = isFill ? (
    <div className={FILL_BOX_CLASS}>{children}</div>
  ) : (
    children
  )

  if (isMobileMode) {
    return (
      // noBodyStyles: vaul's body position-lock collapses the full-height
      // layout chains these surfaces open over (STX1-443).
      <Drawer open={open} onOpenChange={onOpenChange} noBodyStyles>
        {!!trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}
        <DrawerContent
          aria-describedby={undefined}
          className={isFill ? SHEET_FILL_CLASS : SHEET_INSET_CLASS}
        >
          <DrawerTitle className="tw:sr-only">{title}</DrawerTitle>
          {body}
        </DrawerContent>
      </Drawer>
    )
  }

  // Replaces the callsites' `open && !!ref` guards: an anchor ref stays null
  // until the owning bar commits, and there is no trigger to position against.
  if (!trigger && !anchor) {
    return null
  }

  return (
    <OptionsPopoverShell
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      anchor={anchor}
      aria-label={trigger ? undefined : title}
      side={side}
      align={align}
      sideOffset={sideOffset}
      contentClassName={cn(
        width === 'wide' && POPOVER_WIDE_CLASS,
        width === 'picker' && POPOVER_PICKER_CLASS,
        isFill &&
          (width === 'picker' ? POPOVER_PICKER_FILL_CLASS : POPOVER_FILL_CLASS),
      )}
    >
      {body}
    </OptionsPopoverShell>
  )
}

export default ResponsiveSurface
